// Login.js - Enhanced with Signup option
import { useState } from "react";
import api from "../../Services/Api";
import styles from './Login.module.css';

function Login({ onLogin }) {
  const [isLoginMode, setIsLoginMode] = useState(true);
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  const resetForm = () => {
    setUsername('');
    setEmail('');
    setPassword('');
    setConfirmPassword('');
    setError('');
    setSuccessMessage('');
  };
  
  const handleLogin = async () => {
    try {
      const response = await api.post('token/', {
        username,
        password
      });
      
      console.log('Login success:', response.data);
      
      // Store JWT tokens
      localStorage.setItem('token', response.data.access);
      localStorage.setItem('refreshToken', response.data.refresh);
      
      // Store user data
      let userData;
      if (response.data.user) {
        userData = response.data.user;
        localStorage.setItem('username', userData.username);
      } else {
        userData = { 
          username: username, 
          name: username 
        };
      }
      
      console.log('User data:', userData);
      onLogin(userData);
      
    } catch (err) {
      console.error('Login error details:', {
        status: err.response?.status,
        data: err.response?.data,
        message: err.message
      });
      
      if (err.response?.data?.non_field_errors) {
        setError(err.response.data.non_field_errors[0]);
      } else if (err.response?.data?.detail) {
        setError(err.response.data.detail);
      } else if (err.response?.data?.username) {
        setError(err.response.data.username[0]);
      } else if (err.response?.data?.password) {
        setError(err.response.data.password[0]);
      } else if (err.message.includes('Network Error')) {
        setError('Cannot connect to server. Please check your internet connection.');
      } else {
        setError('Login failed. Please check your credentials.');
      }
    }
  };
  
  const handleSignup = async () => {
    // Validate passwords match
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address');
      return;
    }
    
    try {
      // Use your signup endpoint (adjust endpoint if different)
      const response = await api.post('signup/', {
        username,
        email,
        password,
        password2: confirmPassword  // Some APIs use password2 for confirmation
      });
      
      console.log('Signup success:', response.data);

      
      if(response.data.access_token && response.data.refresh_token) {
        localStorage.setItem('token', response.data.access_token);
        localStorage.setItem('refreshToken', response.data.refresh_token);
      localStorage.setItem('username', response.data.user.username);
      
      let userData;
      if(response.data.user) {
        userData = response.data.user;
      } else {
        userData = {
          username: username,
          email: email,
          name : username
        };
      }
      console.log('Auto-login after signup:', userData);
      onLogin(userData); // This will redirect to dashboard
    } else {
      setSuccessMessage('Account created successfully! You can now login.');
      
      setIsLoginMode(true);
      resetForm();
    }
      
    } catch (err) {
      console.error('Signup error details:', {
        status: err.response?.status,
        data: err.response?.data,
        message: err.message
      });
      
      // Handle Django REST Framework validation errors
      if (err.response?.data) {
        const errors = err.response.data;
        
        // Username errors
        if (errors.username) {
          setError(`Username: ${errors.username[0]}`);
        } 
        // Email errors
        else if (errors.email) {
          setError(`Email: ${errors.email[0]}`);
        }
        // Password errors
        else if (errors.password) {
          setError(`Password: ${errors.password[0]}`);
        }
        // Non-field errors (like passwords don't match)
        else if (errors.non_field_errors) {
          setError(errors.non_field_errors[0]);
        }
        // Generic error
        else if (errors.detail) {
          setError(errors.detail);
        }
        // If it's a string error
        else if (typeof errors === 'string') {
          setError(errors);
        }
        // Fallback
        else {
          setError('Signup failed. Please try again.');
        }
      } else if (err.message.includes('Network Error')) {
        setError('Cannot connect to server. Please check your internet connection.');
      } else {
        setError('Signup failed. Please try again.');
      }
    }
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccessMessage('');
    
    try {
      if (isLoginMode) {
        await handleLogin();
      } else {
        await handleSignup();
      }
    } finally {
      setLoading(false);
    }
  };
  
  const toggleMode = () => {
    setIsLoginMode(!isLoginMode);
    resetForm();
  };
  
  return (
    <div className={styles.authContainer}>
      <div className={styles.authCard}>
        <div className={styles.authHeader}>
          <h2 className={styles.title}>
            {isLoginMode ? 'Welcome Back' : 'Create Account'}
          </h2>
          <p className={styles.subtitle}>
            {isLoginMode 
              ? 'Login to track your habits and progress' 
              : 'Sign up to start your habit tracking journey'
            }
          </p>
        </div>
        
        {error && (
          <div className={styles.errorAlert}>
            <span className={styles.errorIcon}>⚠️</span>
            <span>{error}</span>
          </div>
        )}
        
        {successMessage && (
          <div className={styles.successAlert}>
            <span className={styles.successIcon}>✅</span>
            <span>{successMessage}</span>
          </div>
        )}
        
        <form onSubmit={handleSubmit} className={styles.authForm}>
          <div className={styles.inputGroup}>
            <label htmlFor="username" className={styles.label}>
              Username
            </label>
            <input
              id="username"
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              disabled={loading}
              placeholder="Enter your username"
              className={styles.input}
              autoComplete="username"
            />
          </div>
          
          {!isLoginMode && (
            <div className={styles.inputGroup}>
              <label htmlFor="email" className={styles.label}>
                Email
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required={!isLoginMode}
                disabled={loading}
                placeholder="Enter your email"
                className={styles.input}
                autoComplete="email"
              />
            </div>
          )}
          
          <div className={styles.inputGroup}>
            <label htmlFor="password" className={styles.label}>
              Password
            </label>
            <div style={{ position: 'relative' }}>
              <input
                id="password"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={loading}
                placeholder={isLoginMode ? "Enter your password" : "Create a password"}
                className={styles.input}
                autoComplete={isLoginMode ? "current-password" : "new-password"}
              />
              <button
                type="button"
                onClick={() => setShowPassword((prev) => !prev)}
                style={{
                  position: 'absolute',
                  right: 10,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: 0
                }}
                tabIndex={-1}
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? '🙈' : '👁️'}
              </button>
            </div>
          </div>
          
          {!isLoginMode && (
            <div className={styles.inputGroup}>
              <label htmlFor="confirmPassword" className={styles.label}>
                Confirm Password
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  id="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required={!isLoginMode}
                  disabled={loading}
                  placeholder="Confirm your password"
                  className={styles.input}
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword((prev) => !prev)}
                  style={{
                    position: 'absolute',
                    right: 10,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: 0
                  }}
                  tabIndex={-1}
                  aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                >
                  {showConfirmPassword ? '🙈' : '👁️'}
                </button>
              </div>
            </div>
          )}
          
          <button 
            type="submit" 
            disabled={loading || 
              !username || 
              !password || 
              (!isLoginMode && (!email || !confirmPassword))}
            className={`${styles.submitButton} ${
              (loading || !username || !password || 
               (!isLoginMode && (!email || !confirmPassword))) ? styles.disabled : ''
            }`}
          >
            {loading ? (
              <>
                <span className={styles.spinner}></span>
                {isLoginMode ? 'Logging in...' : 'Creating account...'}
              </>
            ) : (
              isLoginMode ? 'Login' : 'Sign Up'
            )}
          </button>
        </form>
        
        <div className={styles.divider}>
          <span>or</span>
        </div>
        
        <div className={styles.authFooter}>
          <p className={styles.modeText}>
            {isLoginMode 
              ? "Don't have an account?" 
              : "Already have an account?"
            }
          </p>
          <button 
            type="button" 
            onClick={toggleMode}
            className={styles.modeToggleButton}
            disabled={loading}
          >
            {isLoginMode ? 'Sign Up' : 'Login'}
          </button>
        </div>
      
      </div>
    </div>
  );
}

export default Login;